// src/utils/constants.js - Updated with course constants
import { API_BASE_URL as RUNTIME_API_BASE_URL } from '../config/runtime';
export const API_BASE_URL = RUNTIME_API_BASE_URL;

/* WHERE THE HOME PAGE'S PREVIEW CHIP STARTS PLAYING.
 *
 *  Not at 0. A lesson opens on its title card being written onto an empty frame, so the
 *  first frames of the preview beside the composer are an empty black box — which is
 *  exactly when a visitor is deciding whether the thing works. Measured across four demos:
 *  at 0.0s every one is 0.00% lit, pure black.
 *
 *  1.5 AND NOT 1. One second clears the black everywhere, but it lands mid-write on the
 *  longer titles — "The Meaning of a Derivative" is still drawing "The Tangent L" with a
 *  half-finished y = f( beside it. At 1.5s that one has its axes, its curve and its
 *  equation complete.
 *
 *  IT IS NOT THE POINT WHERE EVERY TITLE IS FINISHED, and the measurement that first
 *  suggested it was is worth naming so nobody repeats it: the share of lit pixels across
 *  the WHOLE frame plateaus at 1.5s, but that plateau is the curve and the axes finishing,
 *  not the text. Measured on the title band alone (the top 15%), three of the four demos
 *  have settled by 1.5s and "The Meaning of a Derivative" is still writing until 2.0s —
 *  it reads "The Tangent Line & Sl" at 1.5. Raise this to 2 if a settled title matters
 *  more than an opening with motion in it; the trade is the only reason this is a constant
 *  and not a literal.
 *
 *  Applied as a MEDIA FRAGMENT (`#t=`) on the src rather than by seeking after load, and the
 *  difference matters: a fragment tells the browser where to begin, so it range-requests
 *  from that point and the first frame it ever paints is the right one. Setting
 *  `currentTime` on `loadedmetadata` would paint frame 0 first and then jump.
 *
 *  THE PREVIEW ONLY. One caller — `demoVideoSrc` in utils/demoSrc, which is the single
 *  place a demo's url is built. Deliberately NOT inside `apiService.getVideoUrl`, which is
 *  what real playback uses everywhere else: put it there and every video in the product
 *  would skip its first second. */
export const DEMO_PREVIEW_START_SECONDS = 1.5;

/** A demo-preview src: the video URL with the start offset as a media fragment.
 *
 *  `seconds` is the per-lesson value from `config/demoVideos` — see the note on `start`
 *  there for why one number cannot be right for ten different lessons. The constant above
 *  is the fallback for a demo nobody has measured. */
export const demoPreviewSrc = (videoUrl, seconds = DEMO_PREVIEW_START_SECONDS) =>
  `${videoUrl}#t=${seconds}`;

export const PAGES = {
  HOME: 'home',
  AUTH: 'auth',
  CREDITS: 'credits',
  SETTINGS: 'settings',
  BILLING: 'billing',
  WORKSPACE: 'workspace', // Flat chronological timeline of every video made
  HOMEWORK: 'homework',  // Homework sessions
  LECTURE: 'lecture',    // Lecture sessions
  EXPLORE: 'explore',   // Browse/discover content
  CONTACT: 'contact',
  API: 'api',           // Developer API keys + docs
  CREATE: 'create',
  CREATE_COURSE: 'create/course',
  CREATE_LESSON: 'create/lesson',
  SOLVE_PROBLEM: 'create/problem',
  COURSE_DETAIL: 'course-detail',
  // Legacy (kept for backwards compat in Navigation preload logic)
  LIBRARY: 'library',
  PROJECTS: 'projects',
};

// The page -> URL table, shared so the sidebar can render REAL links.
//
// It used to live inside App.setCurrentPage, which meant every nav item was a <button> that
// called a function: no href, so cmd-click, middle-click and "Open link in new tab" all did
// nothing, and right-clicking a nav item gave you the page context menu instead of a link
// one. Anything with a static route belongs here; the parameterised ones (search, the video
// player) stay in App because they need arguments.
export const PAGE_ROUTES = {
  home: '/',
  create: '/create',
  'create/course': '/create/course',
  'create/lesson': '/create/lesson',
  'create/problem': '/create/problem',
  credits: '/credits',
  'credits-success': '/credits/success',
  settings: '/settings',
  billing: '/billing',
  api: '/api',
  workspace: '/workspace',
  explore: '/explore',
  contact: '/contact',
  // Legacy redirects
  projects: '/workspace',
  library: '/workspace',
};

/** The href for a page id, or null when the page has no static route. */
export const routeForPage = (page) => PAGE_ROUTES[page] || null;

/** True when a click should be left to the browser: a new tab, a new window, a download. */
export const isModifiedClick = (e) =>
  e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button !== undefined && e.button !== 0);

// Polling configuration
export const POLLING_CONFIG = {
  INTERVAL: 3000, // 3 seconds
  TIMEOUT: 900000, // 15 minutes
  MAX_CONSECUTIVE_ERRORS: 3
};

export const JOB_STATUS = {
  STARTED: 'started',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

// Generic user-facing error message (never expose backend errors)
export const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// Language options for video narration
export const LANGUAGE_OPTIONS = [
  { code: "en", label: "English", flag: "\u{1F1FA}\u{1F1F8}" },
  { code: "es", label: "Espa\u00f1ol", flag: "\u{1F1EA}\u{1F1F8}" },
  { code: "pt", label: "Portugu\u00eas", flag: "\u{1F1E7}\u{1F1F7}" },
  { code: "ja", label: "\u65E5\u672C\u8A9E", flag: "\u{1F1EF}\u{1F1F5}" },
  { code: "hi", label: "\u0939\u093F\u0928\u094D\u0926\u0940", flag: "\u{1F1EE}\u{1F1F3}" },
  { code: "zh", label: "\u4E2D\u6587", flag: "\u{1F1E8}\u{1F1F3}" },
  { code: "de", label: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}" },
  { code: "fr", label: "Fran\u00e7ais", flag: "\u{1F1EB}\u{1F1F7}" },
  { code: "ru", label: "\u0420\u0443\u0441\u0441\u043A\u0438\u0439", flag: "\u{1F1F7}\u{1F1FA}" },
  { code: "tr", label: "T\u00fcrk\u00e7e", flag: "\u{1F1F9}\u{1F1F7}" },
  { code: "pl", label: "Polski", flag: "\u{1F1F5}\u{1F1F1}" },
  { code: "it", label: "Italiano", flag: "\u{1F1EE}\u{1F1F9}" },
  { code: "ar", label: "\u0627\u0644\u0639\u0631\u0628\u064A\u0629", flag: "\u{1F1F8}\u{1F1E6}" },
  { code: "nl", label: "Nederlands", flag: "\u{1F1F3}\u{1F1F1}" },
  { code: "ko", label: "\uD55C\uAD6D\uC5B4", flag: "\u{1F1F0}\u{1F1F7}" },
];

// Map country codes to default language
const COUNTRY_TO_LANGUAGE = {
  // Spanish-speaking
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es",
  EC: "es", GT: "es", CU: "es", BO: "es", DO: "es", HN: "es", PY: "es",
  SV: "es", NI: "es", CR: "es", PA: "es", UY: "es",
  // Portuguese-speaking
  BR: "pt", PT: "pt", AO: "pt", MZ: "pt",
  // Japanese
  JP: "ja",
  // Hindi
  IN: "hi",
  // Chinese
  CN: "zh", TW: "zh", HK: "zh",
  // German
  DE: "de", AT: "de", CH: "de",
  // French
  FR: "fr", BE: "fr", CA: "fr", SN: "fr", CI: "fr",
  // Russian
  RU: "ru", BY: "ru", KZ: "ru",
  // Turkish
  TR: "tr",
  // Polish
  PL: "pl",
  // Italian
  IT: "it",
  // Arabic
  SA: "ar", AE: "ar", EG: "ar", IQ: "ar", MA: "ar", DZ: "ar", JO: "ar", LB: "ar", KW: "ar", QA: "ar",
  // Dutch
  NL: "nl",
  // Korean
  KR: "ko",
};

export function getDefaultLanguage(countryCode) {
  if (!countryCode) return "en";
  return COUNTRY_TO_LANGUAGE[countryCode.toUpperCase()] || "en";
}
// Countries whose consumers hold a statutory right to withdraw from a distance sale AND whose
// law lets that right be waived for digital content delivered immediately. Showing the waiver
// only here, rather than to everyone, because it is not a harmless extra line: telling someone
// they are giving up a 14-day right they never had is misleading.
//
// Brazil IS included: its Consumer Code allows the right of regret to be forfeited for digital
// content already made available, on the same express-consent-plus-acknowledgement terms as the
// EU — only the window differs (7 days, not 14), which is why the wording below is per-country.
// South Korea is excluded: it has a 7-day right and no confirmed waiver route, so asking a
// Korean customer to give one up would claim something we cannot support.
export const WITHDRAWAL_WAIVER_COUNTRIES = new Set([
  // EU
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT',
  'LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE',
  // EEA (non-EU) + UK + Turkey, which follows the EU model
  'NO','IS','LI','GB','TR',
  // Brazil — right of regret, forfeitable for digital content already supplied
  'BR',
]);

// The statutory window differs by country, and the waiver has to name the right one: telling a
// Brazilian they are giving up "14 days" describes a right they never had.
export const withdrawalWindowDays = (countryCode) =>
  String(countryCode || '').toUpperCase() === 'BR' ? 7 : 14;

// country_code is derived from IP and is null for anyone the lookup missed — it was null for
// almost every user until a deployment bug was fixed in July 2026. Unknown therefore means SHOW
// the waiver: collecting consent nobody needed costs nothing, while skipping it for a German
// customer loses the very thing it exists for.
export const needsWithdrawalWaiver = (countryCode) =>
  !countryCode || WITHDRAWAL_WAIVER_COUNTRIES.has(String(countryCode).toUpperCase());

// The 503s a generation can meet, as messages we own. `code` comes from the backend:
// `servers_busy` is the render queue being full, `at_capacity` is the traffic gate (too many
// people on the site), `maintenance` and `site_halted` are the stop buttons.
// Never render the server's own text — see the standing rule on error display.
//
// NOT EVERY 503 IS A FAILURE. A full queue means "we cannot start your video this minute" —
// nothing is broken, nothing the person did was wrong, and there is nothing to fix. Rendered
// in the red error box it reads as "your request failed", which is both untrue and the kind
// of thing that makes someone leave. `tone` lets the caller draw it as information instead.
export const busyMessageFor = (err) => {
  const code = err?.detail?.code;
  if (code === "servers_busy") {
    return "Our servers are full right now. Come back in a little while and try again.";
  }
  if (code === "at_capacity") {
    return "We're at capacity right now. Please try again in a few minutes.";
  }
  if (code === "generation_down") {
    return "Video generation is down right now. Nothing you have made is affected.";
  }
  if (code === "maintenance" || code === "site_halted") {
    return "Magi is offline for maintenance. Please try again shortly.";
  }
  return "Our servers are busy right now. Come back in a little while and try again.";
};

/* WHY A GENERATION DIED, IN ONE SENTENCE A PERSON CAN ACT ON.
 *
 * The card and the banner used to say "Video failed" and stop there, which leaves the only
 * useful question unanswered: is this me or is this you? "A scene wouldn't render" means try
 * again or reword it; "our render machines didn't pick it up" means nothing you type will
 * help and the queue is the problem. The backend error is written for us — it carries call
 * ids, timeouts and stack context — and must never be shown raw, so this maps the ones we
 * actually produce onto plain sentences.
 *
 * Returns null for anything unrecognised, and the caller shows nothing rather than guessing.
 * A wrong reason is worse than no reason: it sends somebody to rewrite a prompt that was
 * never the problem.
 */
export const failureReasonFor = (error) => {
  const text = String(error || "");
  if (!text) return null;
  if (/no result from the render worker|waiting for a free gpu|had no container/i.test(text)) {
    return "Our render machines were busy and didn't pick it up in time.";
  }
  if (/narration script|script generation/i.test(text)) {
    return "The script for the video couldn't be written.";
  }
  if (/credit balance|invalid_request_error|unauthorized|api key/i.test(text)) {
    return "One of the services we rely on refused the request.";
  }
  if (/scene\(s\) failed|rendering failed|manim/i.test(text)) {
    return "One of the scenes wouldn't render.";
  }
  if (/timed out|timeout/i.test(text)) {
    return "It took longer than we allow and we stopped it.";
  }
  if (/cancel/i.test(text)) return null;        // they did this on purpose
  return null;
};

// 'info' for "come back later", 'error' for "something went wrong". Only the stop buttons are
// the second kind, and barely — but a site that is deliberately off is a different statement
// from a queue that is briefly full, and the person waiting for the second one should not be
// told the first.
export const busyToneFor = (err) => (
  ["maintenance", "site_halted", "generation_down"].includes(err?.detail?.code) ? "error" : "info"
);

// ── Minimum sign-up age ─────────────────────────────────────────────────────────────────
//
// The Terms say "Users must be older than 13", and that is the floor everywhere. What varies
// is GDPR Art. 8: a child below their country's DIGITAL AGE OF CONSENT cannot consent for
// themselves, and member states were allowed to set that anywhere from 13 to 16. So the line
// under the sign-in button states the age that is actually true where the visitor is, rather
// than quoting 13 at a German 14-year-old for whom it is wrong.
//
// SCOPE, deliberately narrow: this is a DISCLOSURE, not the deleted age gate (33cb8fe9). It
// changes what the sentence says and nothing else — no birthday field, no block, no stored
// age. Collecting a child's birth date to decide whether we may hold a child's data is the
// trade that flow lost on, and it is not being rebuilt for a line of copy.
//
// Ages below are the Art. 8(1) national choices. They move occasionally (Spain 13 -> 14 in
// 2018 is the usual example), so treat this table as needing a check before any EU push
// rather than as settled fact. Anywhere not listed falls through to 13, which matches both
// the Terms and the US COPPA line.
const DIGITAL_CONSENT_AGE = {
  // 16 — the Art. 8(1) default, kept by these
  DE: 16, NL: 16, IE: 16, PL: 16, HU: 16, HR: 16, LU: 16, RO: 16, SK: 16, LI: 16,
  // 15
  CZ: 15, FR: 15, GR: 15, SI: 15,
  // 14
  AT: 14, BG: 14, CY: 14, IT: 14, LT: 14, ES: 14,
  // 13 — listed explicitly so the table reads as a decision rather than an omission
  BE: 13, DK: 13, EE: 13, FI: 13, LV: 13, MT: 13, PT: 13, SE: 13, GB: 13, NO: 13, IS: 13,
};

// EEA/UK timezones only. The full IANA table is ~420 zones and every one of them outside this
// list resolves to 13 anyway, so carrying it would be weight for no behaviour. A zone shared
// by several countries resolves to whichever is listed — the alternative is no country at all.
const TZ_TO_COUNTRY = {
  "Europe/Vienna": "AT", "Europe/Brussels": "BE", "Europe/Sofia": "BG", "Europe/Zagreb": "HR",
  "Asia/Nicosia": "CY", "Europe/Nicosia": "CY", "Europe/Prague": "CZ", "Europe/Copenhagen": "DK",
  "Europe/Tallinn": "EE", "Europe/Helsinki": "FI", "Europe/Paris": "FR", "Europe/Berlin": "DE",
  "Europe/Busingen": "DE", "Europe/Athens": "GR", "Europe/Budapest": "HU", "Europe/Dublin": "IE",
  "Europe/Rome": "IT", "Europe/Riga": "LV", "Europe/Vilnius": "LT", "Europe/Luxembourg": "LU",
  "Europe/Malta": "MT", "Europe/Amsterdam": "NL", "Europe/Warsaw": "PL", "Europe/Lisbon": "PT",
  "Atlantic/Azores": "PT", "Atlantic/Madeira": "PT", "Europe/Bucharest": "RO",
  "Europe/Bratislava": "SK", "Europe/Ljubljana": "SI", "Europe/Madrid": "ES",
  "Atlantic/Canary": "ES", "Europe/Ceuta": "ES", "Europe/Stockholm": "SE", "Europe/London": "GB",
  "Europe/Oslo": "NO", "Atlantic/Reykjavik": "IS", "Europe/Vaduz": "LI",
};

// Browser-derived, because this renders BEFORE sign-in — there is no user doc and therefore no
// country_code to read. Timezone first (it describes where the device IS), then the locale's
// region subtag as a fallback. Both are client-supplied and trivially spoofed; that is fine,
// because nothing is gated on the answer. The worst case is a sentence quoting the wrong
// number to someone who went out of their way to be quoted the wrong number.
export const browserCountry = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && TZ_TO_COUNTRY[tz]) return TZ_TO_COUNTRY[tz];
  } catch { /* ancient/locked-down browser — fall through to the locale */ }
  try {
    const parts = String(navigator.language || "").replace("_", "-").split("-");
    for (let i = parts.length - 1; i >= 1; i--) {
      if (/^[A-Za-z]{2}$/.test(parts[i])) return parts[i].toUpperCase();
    }
  } catch { /* ditto — 13 is the safe answer and the Terms' own number */ }
  return null;
};

// 13 for everyone the table does not name, which is the Terms' floor and the US COPPA line.
export const minimumSignupAge = (countryCode) =>
  DIGITAL_CONSENT_AGE[String(countryCode || "").toUpperCase()] || 13;
